<template>
  <div class="applicant-notes">
    <p v-if="loading" class="text-muted small mb-2">Loading notes...</p>
    <p v-else-if="!notes.length" class="text-muted small mb-2 no-notes">
      No notes yet.
    </p>

    <div v-for="note in notes" :key="note.id" class="note-row">
      <div class="note-meta d-flex justify-content-between">
        <span>
          {{ note.author || 'Former team member' }} &middot;
          {{ formatDateTime(note.created_at) }}
        </span>
        <b-link
          v-if="canManage"
          class="delete-note-link"
          :disabled="deletingId === note.id"
          @click.prevent="remove(note)"
        >
          Delete
        </b-link>
      </div>
      <!-- Plain text only, never rendered as HTML. -->
      <div class="note-body">{{ note.body }}</div>
    </div>

    <div v-if="canManage" class="note-form">
      <b-form-textarea
        v-model="draft"
        rows="2"
        max-rows="6"
        :maxlength="maxLength"
        placeholder="Add a private note. The applicant cannot see it."
        class="note-input"
      />
      <b-button
        size="sm"
        variant="outline-primary"
        class="add-note-btn mt-2"
        :disabled="saving || !draft.trim()"
        @click="add"
      >
        Add note
      </b-button>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import apiToasts from '@/mixins/apiToasts'
import { parseServerDate } from '@/utils/dateHelper'

// Private company-side notes on one applicant (never shown to the tradie).
// Loads when first shown, so a long applicant list costs no extra requests.
export default {
  name: 'ApplicantNotes',

  mixins: [apiToasts],

  props: {
    jobId: {
      type: [Number, String],
      required: true,
    },
    applicationId: {
      type: [Number, String],
      required: true,
    },
    // jobs.edit: may add and delete notes. Without it the notes are read-only.
    canManage: Boolean,
  },

  data() {
    return {
      loading: false,
      saving: false,
      deletingId: null,
      notes: [],
      draft: '',
      maxLength: 2000,
    }
  },

  created() {
    this.load()
  },

  methods: {
    ...mapActions('jobPosting', [
      'fetchApplicationNotes',
      'addApplicationNote',
      'deleteApplicationNote',
    ]),

    formatDateTime(value) {
      const d = parseServerDate(value)
      if (!d) return '-'
      return d.toLocaleString('en-AU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    ids() {
      return { jobId: this.jobId, applicationId: this.applicationId }
    },

    async load() {
      this.loading = true
      try {
        this.notes = await this.fetchApplicationNotes(this.ids())
      } catch (err) {
        this.onError(err, 'Could not load the notes.')
      } finally {
        this.loading = false
      }
    },

    async add() {
      const body = this.draft.trim()
      if (!body) return
      this.saving = true
      try {
        const note = await this.addApplicationNote({ ...this.ids(), body })
        this.notes = [note, ...this.notes]
        this.draft = ''
      } catch (err) {
        this.onError(err, 'Could not save this note.')
      } finally {
        this.saving = false
      }
    },

    async remove(note) {
      this.deletingId = note.id
      try {
        await this.deleteApplicationNote({ ...this.ids(), noteId: note.id })
        this.notes = this.notes.filter((n) => n.id !== note.id)
      } catch (err) {
        this.onError(err, 'Could not delete this note.')
      } finally {
        this.deletingId = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Gray values follow the newer Tailwind-style scale in DESIGN.md.
.applicant-notes {
  margin-top: 0.9rem;
  padding: 0.9rem 1rem;
  background-color: #f9fafb;
  border-radius: 8px;
}

.note-row {
  padding-bottom: 0.6rem;
  margin-bottom: 0.6rem;
  border-bottom: 1px solid #e5e7eb;
}

.note-meta {
  font-size: 0.75rem;
  color: #6b7280;
}

.note-body {
  margin-top: 0.15rem;
  color: #374151;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.delete-note-link {
  font-size: 0.75rem;
}
</style>
